import { useState } from "react";
import { Link } from "react-router-dom";
import { categories, articles, featuredDestinations, FAQData } from "@/mocks/data";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className="w-4 h-4 flex items-center justify-center">
          <i className={`text-xs ${i < Math.floor(rating) ? "ri-star-fill text-primary-500" : i < rating ? "ri-star-half-fill text-primary-500" : "ri-star-line text-foreground-300"}`}></i>
        </span>
      ))}
    </div>
  );
}

export default function Home() {
  const [activeCat, setActiveCat] = useState("all");

  const filteredArticles = activeCat === "all"
    ? articles.slice(0, 6)
    : articles.filter((a) => a.category === activeCat).slice(0, 6);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Breathtaking%20aerial%20view%20of%20a%20tropical%20Asian%20landscape%20with%20emerald%20green%20rice%20terraces%20winding%20river%20ancient%20temple%20in%20distance%20golden%20sunrise%20light%20morning%20mist%20rolling%20over%20mountains%20lush%20vegetation%20serene%20atmosphere%20award%20winning%20travel%20photography%20warm%20golden%20tones%20dreamy%20atmosphere&width=1800&height=900&seq=hero-main-01&orientation=landscape"
          alt="亚洲天堂 - 探索最美亚洲旅游胜地"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
          <p className="text-white/70 text-sm md:text-base tracking-widest uppercase mb-3">
            探索东方之美
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white text-center leading-tight mb-4 max-w-4xl">
            亚洲天堂
          </h1>
          <p className="text-white/80 text-sm md:text-lg text-center max-w-2xl mb-8">
            汇聚亚洲最令人心动的旅游目的地与度假胜地，带你领略东方最极致的自然风光与人文魅力
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/redai-haidao"
              className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              探索目的地
            </Link>
            <Link
              to="/about"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              探索旅行主题
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              无论你向往碧海蓝天、千年古迹还是繁华都市，这里都有属于你的旅行灵感
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center p-5 rounded-xl bg-background-100 hover:bg-primary-50 transition-all cursor-pointer border border-transparent hover:border-primary-200/50"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3 group-hover:bg-primary-500 group-hover:text-white transition-all">
                  <i className={`${cat.icon} text-xl`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                  {cat.name}
                </span>
                <span className="text-xs text-foreground-400 mt-1">
                  {cat.count}篇攻略
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              热门目的地
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              精选亚洲最受欢迎的旅行目的地，开启你的下一段旅程
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredDestinations.map((dest) => (
              <Link
                key={dest.slug}
                to={`/category/${dest.categorySlug}`}
                className="group relative rounded-xl overflow-hidden bg-white cursor-pointer h-[320px]"
              >
                <img
                  src={dest.imageUrl}
                  alt={`${dest.name} - ${dest.description}`}
                  title={`${dest.name} - ${dest.description}`}
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-white/70 text-xs mb-1">{dest.country}</p>
                  <h3 className="font-heading text-xl font-bold text-white mb-1">
                    {dest.name}
                  </h3>
                  <p className="text-white/70 text-sm mb-2">{dest.englishName}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StarRating rating={dest.rating} />
                      <span className="text-white/70 text-xs">{dest.rating}</span>
                    </div>
                    <span className="text-white/60 text-xs">{dest.articleCount}篇攻略</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              精选旅行攻略
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              由资深旅行家精心撰写的深度攻略，让每一次旅行都不留遗憾
            </p>
          </div>

          <div className="flex items-center justify-center flex-wrap gap-2 mb-8">
            {[
              { key: "all", label: "全部" },
              ...categories.slice(0, 4).map((c) => ({ key: c.slug, label: c.name })),
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveCat(tab.key)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCat === tab.key
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.cardImageUrl}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-accent-500 text-white text-xs px-2.5 py-1 rounded-full">
                      {categories.find((c) => c.slug === article.category)?.name || article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <div className="flex items-center gap-3">
                      <span>{article.author}</span>
                      <span>{article.publishDate}</span>
                    </div>
                    <span>{article.readTime}阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/category/redai-haidao"
              className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              查看全部攻略
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-primary-500">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "30+", label: "亚洲国家与地区" },
              { value: "200+", label: "精选目的地" },
              { value: "500+", label: "深度旅行攻略" },
              { value: "50万+", label: "月活跃旅行者" },
            ].map((stat, i) => (
              <div key={i}>
                <div className="font-heading text-3xl md:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              关于亚洲天堂旅行的常见疑问解答
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQData.slice(0, 4).map((faq, i) => (
              <details
                key={i}
                className="group bg-white rounded-xl border border-background-200/70 overflow-hidden cursor-pointer"
              >
                <summary className="flex items-center justify-between p-5 font-medium text-foreground-800 text-sm list-none">
                  {faq.question}
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-500 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}